import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, DatePicker, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  productionPlanningService,
  type MasterScheduleRow,
  type MasterScheduleWorkOrder,
} from '@/services/production-planning.service';

const { Title, Text } = Typography;

const WorkOrderDates: React.FC<{ order: MasterScheduleWorkOrder }> = ({ order }) => {
  const queryClient = useQueryClient();
  const [start, setStart] = useState(order.planned_start_date);
  const [end, setEnd] = useState(order.planned_end_date);
  const save = useMutation({
    mutationFn: () => productionPlanningService.moveWorkOrderDates(order.id, start || '', end || ''),
    onSuccess: () => {
      message.success(`${order.work_order_number} dates updated`);
      queryClient.invalidateQueries({ queryKey: ['production-planning', 'master-schedule'] });
    },
    onError: (error: Error) => message.error(error.message),
  });

  return (
    <Space>
      <Link to={`/mfg/work-orders/${order.id}`}>{order.work_order_number}</Link>
      <Text>{order.planned_quantity}</Text>
      <Text type="secondary">{order.work_order_status}</Text>
      <DatePicker
        value={start ? dayjs(start) : null}
        format="DD-MMM-YYYY"
        onChange={(value) => setStart(value ? value.format('YYYY-MM-DD') : null)}
      />
      <DatePicker
        value={end ? dayjs(end) : null}
        format="DD-MMM-YYYY"
        onChange={(value) => setEnd(value ? value.format('YYYY-MM-DD') : null)}
      />
      <Button size="small" type="primary" loading={save.isPending} disabled={!start || !end} onClick={() => save.mutate()}>
        Move
      </Button>
    </Space>
  );
};

const MasterSchedulePage: React.FC = () => {
  const query = useQuery({
    queryKey: ['production-planning', 'master-schedule'],
    queryFn: () => productionPlanningService.masterSchedule(8),
  });
  const schedule = query.data;

  const columns: ColumnsType<MasterScheduleRow> = [
    {
      title: 'Item',
      key: 'item',
      fixed: 'left',
      width: 240,
      render: (_, row) => (
        <div>
          <div>{row.item_code || '—'}</div>
          <Text type="secondary">{row.item_name}</Text>
        </div>
      ),
    },
    ...(schedule?.week_starts || []).map((week, index) => ({
      title: dayjs(week).format('DD MMM'),
      key: week,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, row: MasterScheduleRow) => {
        const cell = row.weeks[index];
        if (!cell || (cell.demand_qty === 0 && cell.work_order_qty === 0)) return '—';
        return (
          <div>
            <div>{cell.demand_qty}</div>
            <Text type="secondary">{cell.work_order_qty} on WO</Text>
          </div>
        );
      },
    })),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        Master production schedule
      </Title>
      <Text type="secondary">
        Firm demand for the next eight weeks: open sales-order quantity by the promise date, plus production-request quantity that is not on a work order yet. The second number is work-order quantity whose planned end falls in that week. Move a work order to change the plan. Releasing work stays on the sales order and production request.
      </Text>
      <Alert
        style={{ margin: '16px 0' }}
        type="info"
        showIcon
        message="This schedule does not include a statistical forecast. Quotations stay in the pipeline until they become an order or a production request."
      />
      <Card>
        <Table
          rowKey="item_key"
          loading={query.isLoading}
          dataSource={schedule?.rows || []}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 20 }}
          expandable={{
            expandedRowRender: (row) =>
              row.work_orders.length ? (
                <Space direction="vertical">
                  {row.work_orders.map((order) => (
                    <WorkOrderDates key={order.id} order={order} />
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No open work order for this item.</Text>
              ),
            rowExpandable: () => true,
          }}
        />
      </Card>
    </div>
  );
};

export default MasterSchedulePage;
